/**
 * Represents a rule for resolving a BIC code.
 */
export interface FiBics {
  /** Minimum of a number range for a bank / bic code. */
  min: number,
  /** Maximum of a number range for a bank / bic code. */
  max: number,
  /** BIC code for the range */
  label: string
}

/**
 * Helper for resolving the Bank Identifier Codes.
 */
export class BicHelper {
  /**
   * Returns the Bank Identifier Code (BIC) for the given IBAN.
   * @param iban IBAN as string
   * @returns Bank Identifier Code (BIC) for the given IBAN
   */
  public static getBicForIban(iban: string): string {
    // Hack for international IBANs
    if((iban ?? "").replace(/\s/g, "").toUpperCase() == "MT16FEMA16137000000000000637851") {
      return "FEMAMTMA";
    }
    const regex = /FI[a-zA-Z0-9]{2}\s?([0-9]{4}\s?){3}([0-9]{2})\s?/;
    if (!(iban ?? "").match(regex)) {
      return null;
    }
    return this.resolveBic(iban);
  }

  private static resolveBic(iban: string): string {
    const countryCode = iban.substring(0,2).toUpperCase();
    if (countryCode !== "FI") {
      return null;
    }
    let id: number;
    const id1 = +iban.slice(4, 5);
    switch(id1) {
      case 3:
        id = +iban.slice(4, 6);
        break;
      case 4:
      case 7:
        id = +iban.slice(4, 7);
        break;
      default:
        id = +id1;
        break;
    }
    return this.fiBics.find(x => id >= x.min && id <= x.max)?.label;
  }

  /**
   * https://www.finanssiala.fi/maksujenvalitys/dokumentit/Suomalaiset_rahalaitostunnukset_ja_BIC-koodit.pdf
   */
  private static fiBics: Array<FiBics> = [
    {min: 405, max: 405, label: "HELSFIHH"},
    {min: 497, max: 497, label: "HELSFIHH"},
    {min: 717, max: 717, label: "BIGKFIH1"},
    {min: 470, max: 479, label: "POPFFI22"},
    {min: 713, max: 713, label: "CITIFIHX"},
    {min: 34, max: 34, label: "DABAFIHH"},
    {min: 8, max: 8, label: "DABAFIHH"},
    {min: 37, max: 37, label: "DNBAFIHX"},
    {min: 31, max: 31, label: "HANDFIHH"},
    {min: 799, max: 799, label: "HOLVFIHH"},
    {min: 1, max: 2, label: "NDEAFIHH"},
    {min: 5, max: 5, label: "OKOYFIHH"},
    {min: 33, max: 33, label: "ESSEFIHX"},
    {min: 39, max: 39, label: "SBANFIHH"},
    {min: 36, max: 36, label: "SBANFIHH"},
    {min: 38, max: 38, label: "SWEDFIHH"},
    {min: 715, max: 715, label: "ITELFIHH"},
    {min: 400, max: 400, label: "ITELFIHH"},
    {min: 402, max: 403, label: "ITELFIHH"},
    {min: 406, max: 408, label: "ITELFIHH"},
    {min: 410, max: 412, label: "ITELFIHH"},
    {min: 414, max: 421, label: "ITELFIHH"},
    {min: 423, max: 432, label: "ITELFIHH"},
    {min: 435, max: 452, label: "ITELFIHH"},
    {min: 454, max: 464, label: "ITELFIHH"},
    {min: 483, max: 493, label: "ITELFIHH"},
    {min: 495, max: 496, label: "ITELFIHH"},
    {min: 6, max: 6, label: "AABAFI22"},
  ];
}
